import * as React from 'react';
import { TextField, Button, TextSelect, NumberSelect } from '../common';
import { BaseDialog, BaseDialogProps, BaseDialogState } from '../common/dialog/BaseDialog';
import { Grid2 as Grid } from '@mui/material';
import { getVersionsViews } from '../../utils';

interface Props extends BaseDialogProps {
    readonly version: string;
    readonly subVersion?: string;
    readonly cloudCodeRevisions: Entities.CloudCode[];
    readonly titleDataVersions: Entities.TitleData[];
    readonly contentVersions: Entities.FileVersion[];
    readonly onCreate: (version: string, clientVersionOverride: Entities.ClientVersionOverride) => void;
}

interface State extends BaseDialogState {
    readonly subVersion: string;
    readonly cloudCodeRevision: number;
    readonly titleDataVersion: string;
    readonly contentVersion: string;
}

export class CreateClientVersionOverrideDialog extends BaseDialog<Props, State> {
    state = this.initState();

    componentDidUpdate(prevProps: Props) {
        if (this.props.version !== prevProps.version) {
            this.setState(this.initState());
        }
    }

    protected renderContent(): React.JSX.Element {
        const { subVersion, cloudCodeRevisions, titleDataVersions, contentVersions } = this.props;
        const { cloudCodeRevision, titleDataVersion, contentVersion } = this.state;

        const versionsViews = getVersionsViews(cloudCodeRevisions, titleDataVersions, contentVersions, true);
        return (
            <Grid container={true} spacing={0}>
                {!subVersion && (
                    <Grid size={12}>
                        <TextField
                            label="Sub Version"
                            fullWidth={true}
                            value={subVersion}
                            required={true}
                            onChange={this.updateSubVersion}
                            variant="outlined"
                        />
                    </Grid>
                )}
                <Grid size={12}>
                    <NumberSelect
                        label="Cloud Code"
                        value={cloudCodeRevision}
                        onChange={this.handleCloudCodeRevisionChange}
                    >
                        {versionsViews.cloudCodesViews}
                    </NumberSelect>
                </Grid>
                <Grid size={12}>
                    <TextSelect
                        label="Title Data"
                        value={titleDataVersion}
                        onChange={this.handleTitleDataVersionChange}
                    >
                        {versionsViews.titleDataVersionsViews}
                    </TextSelect>
                </Grid>
                <Grid size={12}>
                    <TextSelect
                        label="Content"
                        value={contentVersion}
                        onChange={this.handleContentVersionChange}
                    >
                        {versionsViews.contentVersionsViews}
                    </TextSelect>
                </Grid>
            </Grid>
        );

    }

    protected renderActions(): React.JSX.Element {
        return (
            <Button text="Create" onClick={this.onSubmit} variant="contained" disabled={this.validate()} />
        );
    }

    protected initState(): State {
        const { subVersion } = this.props;
        return {
            subVersion: subVersion || '',
            cloudCodeRevision: -1,
            titleDataVersion: "",
            contentVersion: "",
        };
    }

    protected handleCloudCodeRevisionChange = (cloudCodeRevision: number) => this.setState({ cloudCodeRevision });

    protected handleTitleDataVersionChange = (titleDataVersion: string) => this.setState({ titleDataVersion });

    protected handleContentVersionChange = (contentVersion: string) => this.setState({ contentVersion});

    private updateSubVersion = (subVersion: string) => this.setState({subVersion});

    private onSubmit = () => {
        const { version } = this.props;
        const { subVersion, cloudCodeRevision, titleDataVersion, contentVersion } = this.state;
        if (this.validate()) {
            return;
        }
        const clientVersionOverride: Entities.ClientVersionOverride = {
            version,
            subVersion,
            cloudCodeRevision,
            titleDataVersion,
            contentVersion,
        };
        this.props.onCreate(version, clientVersionOverride);
        this.onClose();
    };

    private validate = () => {
        const { subVersion, cloudCodeRevision, titleDataVersion, contentVersion } = this.state;
        return subVersion === '' && cloudCodeRevision === -1 && titleDataVersion === "" && contentVersion === "";
    };
}
